import {
  FINANCE_LINE_DESCRIPTION_MAX_LENGTH,
  FINANCE_QUANTITY_PATTERN,
  FINANCE_SIGNED_UNIT_PRICE_PATTERN,
  FINANCE_UNIT_PRICE_PATTERN,
} from '@/modules/finance/common/constants/line-item-validation.constants';

export enum InvoiceLineValidationIssueCode {
  MissingMember = 'missingMember',
  InvalidQuantity = 'invalidQuantity',
  InvalidUnitPrice = 'invalidUnitPrice',
  ZeroMemberLine = 'zeroMemberLine',
  EmptyDescription = 'emptyDescription',
  DescriptionTooLong = 'descriptionTooLong',
}

export enum InvoiceValidationIssueCode {
  EmptyItems = 'emptyItems',
  NegativeTotal = 'negativeTotal',
}

export enum InvoiceLineValidationInputType {
  Member = 'member',
  Overhead = 'overhead',
}

export type InvoiceLineValidationField = 'memberId' | 'description' | 'quantity' | 'unitPrice';

export interface InvoiceLineValidationIssue {
  code: InvoiceLineValidationIssueCode;
  field: InvoiceLineValidationField;
}

interface InvoiceMemberLineValidationInput {
  type: InvoiceLineValidationInputType.Member;
  /** Omit for existing members whose identity is guaranteed by the source row. */
  memberId?: string | number | null;
  quantity: string | number;
  unitPrice: string | number;
}

interface InvoiceOverheadLineValidationInput {
  type: InvoiceLineValidationInputType.Overhead;
  description: string;
  unitPrice: string | number;
}

export type InvoiceLineValidationInput =
  | InvoiceMemberLineValidationInput
  | InvoiceOverheadLineValidationInput;

interface InvoiceMemberLineValues {
  quantity: number;
  unitPrice: number;
}

/** A member line is invalid only when both factors producing its amount are zero. */
export const isInvoiceMemberLineZero = ({
  quantity,
  unitPrice,
}: InvoiceMemberLineValues): boolean => quantity === 0 && unitPrice === 0;

export const isInvoiceLineDescriptionEmpty = (description: string): boolean =>
  description.trim() === '';

export const isInvoiceLineDescriptionTooLong = (description: string): boolean =>
  description.trim().length > FINANCE_LINE_DESCRIPTION_MAX_LENGTH;

export const isInvoiceTotalNegative = (total: number): boolean => total < 0;

export const isInvoiceMemberMissing = (memberId: string | number | null | undefined): boolean => {
  if (memberId === undefined || memberId === null) return true;
  if (typeof memberId === 'number') return !Number.isInteger(memberId) || memberId <= 0;
  return !/^\d+$/.test(memberId) || Number(memberId) <= 0;
};

const matchesNumberPattern = (value: string | number, pattern: RegExp): boolean =>
  typeof value === 'number'
    ? Number.isFinite(value) && pattern.test(String(value))
    : pattern.test(value);

export function validateInvoiceLine(
  input: InvoiceLineValidationInput
): InvoiceLineValidationIssue[] {
  if (input.type === InvoiceLineValidationInputType.Member) {
    const issues: InvoiceLineValidationIssue[] = [];

    if ('memberId' in input && isInvoiceMemberMissing(input.memberId)) {
      issues.push({
        code: InvoiceLineValidationIssueCode.MissingMember,
        field: 'memberId',
      });
    }
    if (!matchesNumberPattern(input.quantity, FINANCE_QUANTITY_PATTERN)) {
      issues.push({
        code: InvoiceLineValidationIssueCode.InvalidQuantity,
        field: 'quantity',
      });
    }
    if (!matchesNumberPattern(input.unitPrice, FINANCE_UNIT_PRICE_PATTERN)) {
      issues.push({
        code: InvoiceLineValidationIssueCode.InvalidUnitPrice,
        field: 'unitPrice',
      });
    }
    if (
      isInvoiceMemberLineZero({
        quantity: Number(input.quantity),
        unitPrice: Number(input.unitPrice),
      })
    ) {
      issues.push(
        { code: InvoiceLineValidationIssueCode.ZeroMemberLine, field: 'quantity' },
        { code: InvoiceLineValidationIssueCode.ZeroMemberLine, field: 'unitPrice' }
      );
    }

    return issues;
  }

  const issues: InvoiceLineValidationIssue[] = [];

  if (isInvoiceLineDescriptionEmpty(input.description)) {
    issues.push({
      code: InvoiceLineValidationIssueCode.EmptyDescription,
      field: 'description',
    });
  } else if (isInvoiceLineDescriptionTooLong(input.description)) {
    issues.push({
      code: InvoiceLineValidationIssueCode.DescriptionTooLong,
      field: 'description',
    });
  }
  if (!matchesNumberPattern(input.unitPrice, FINANCE_SIGNED_UNIT_PRICE_PATTERN)) {
    issues.push({
      code: InvoiceLineValidationIssueCode.InvalidUnitPrice,
      field: 'unitPrice',
    });
  }

  return issues;
}

export const getInvoiceLineAmount = (input: InvoiceLineValidationInput): number => {
  const amount =
    input.type === InvoiceLineValidationInputType.Member
      ? Number(input.quantity) * Number(input.unitPrice)
      : Number(input.unitPrice);

  return Number.isFinite(amount) ? amount : 0;
};

export const validateInvoiceItems = (
  items: InvoiceLineValidationInput[]
): InvoiceValidationIssueCode[] => {
  const issues: InvoiceValidationIssueCode[] = [];

  if (items.length === 0) {
    issues.push(InvoiceValidationIssueCode.EmptyItems);
  }

  const total = items.reduce((sum, item) => sum + getInvoiceLineAmount(item), 0);
  if (isInvoiceTotalNegative(total)) {
    issues.push(InvoiceValidationIssueCode.NegativeTotal);
  }

  return issues;
};
